import { FireTVCampaignAutomationClient } from '@amzn/fire-tv-campaign-automation-service';
import { Context, createContext, useContext } from 'react';

import { validateClient } from '../utils/clients/clientUtils';

interface ClientContextType {
  controlPlaneClient: FireTVCampaignAutomationClient | undefined;
  // Add other clients here as needed
}

/**
 * React Context for consuming various API clients.
 * Initialized with default undefined clients that will be set by the Provider.
 */
const ClientContext: Context<ClientContextType> = createContext<ClientContextType>({
  controlPlaneClient: undefined,
  // Initialize other clients here
});

/**
 * Custom hook to access the Client Context.
 * @throws {Error} If used outside of a ClientProvider
 * @returns {ClientContextType} The client context value containing all available clients
 */
const useClient = (): ClientContextType => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

/**
 * Get a validated control plane client
 * @throws {Error} If the control plane client is not defined
 * @returns {FireTVCampaignAutomationClient} The validated control plane client
 */
export const getControlPlaneClient = (): FireTVCampaignAutomationClient => {
  const { controlPlaneClient } = useClient();
  return validateClient(controlPlaneClient, 'Control plane client');
};

// Add other client getters here as needed
// export const getOtherClient = (): OtherClientType => {
//   const { otherClient } = useClient();
//   return validateClient(otherClient, 'Other client');
// };

export { ClientContext, useClient };
